import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, Globe, TrendingUp, AlertCircle, CheckCircle, Loader, Info } from 'lucide-react';
import { authService } from '../services/authService';
import { SeoReport } from '../types';
import { t } from '../services/i18n';

interface ReportingProps {
  currentUser: any;
}

export const Reporting: React.FC<ReportingProps> = ({ currentUser }) => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      // Get user's activity history to find all audits
      const activity = await authService.getMyActivity();
      const audits = activity.filter((act: any) => act.action === 'audit');
      
      // Map audits with full report data from metadata
      setReports(audits.map((audit: any) => ({
        id: audit._id || audit.id,
        url: audit.resource,
        date: audit.createdAt,
        action: audit.action,
        report: audit.metadata?.fullReport || null, // Full report stored in metadata
        score: audit.metadata?.score || 0
      })));
    } catch (error: any) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (reportData: SeoReport) => {
    if (!reportData) return;
    
    setGeneratingPDF(reportData.url);
    try {
      const blob = await authService.generatePDFReport(reportData);
      const url = window.URL.createObjectURL(blob);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", `orca-seo-report-${reportData.url.replace(/https?:\/\//, '').replace(/\//g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('PDF generation error:', error);
      alert(`Failed to generate PDF: ${error.message || 'Unknown error'}`);
    } finally {
      setGeneratingPDF(null);
    }
  };

  return (
    <div className="p-8 animate-fadeIn text-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <FileText className="text-indigo-400" size={30} />
              {t('reporting.title') || 'SEO Reports & Analytics'}
            </h2>
            <p className="text-slate-400 mt-2">
              {t('reporting.subtitle') || 'View and download comprehensive SEO audit reports'}
            </p>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-indigo-400 mb-2">
                {t('reporting.infoTitle') || 'About Reports'}
              </h3>
              <p className="text-slate-300 text-sm">
                {t('reporting.infoText') || 'Generate comprehensive PDF reports containing all SEO analysis data including On-Page, Technical, Off-Page, and AEO scores, issues, and recommendations. Reports are generated from your website audit data.'}
              </p>
            </div>
          </div>
        </div>

        {/* Reports List */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="text-blue-400" size={24} />
            {t('reporting.auditHistory') || 'Audit History'}
          </h3>

          {loading ? (
            <div className="text-center py-12">
              <Loader className="animate-spin mx-auto mb-4 text-indigo-400" size={32} />
              <p className="text-slate-400">{t('common.loading')}</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto mb-4 text-slate-500" size={48} />
              <p className="text-slate-400 mb-2">{t('reporting.noReports') || 'No audit reports yet'}</p>
              <p className="text-slate-500 text-sm">
                {t('reporting.noReportsDesc') || 'Run a site audit to generate your first report'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-slate-900/50 rounded-lg border border-slate-700 p-4 flex items-center justify-between hover:bg-slate-900 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="p-3 bg-indigo-500/20 rounded-lg">
                      <Globe className="text-indigo-400" size={20} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">{report.url}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-xs text-slate-400">
                          {new Date(report.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {report.score !== undefined && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            report.score >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                            report.score >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            Score: {report.score}/100
                          </span>
                        )}
                        {!report.report && (
                          <span className="text-xs text-slate-500">(No report data)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {report.report ? (
                      <button
                        onClick={() => handleGeneratePDF(report.report)}
                        disabled={generatingPDF === report.url}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingPDF === report.url ? (
                          <>
                            <Loader className="animate-spin" size={16} />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            {t('reporting.generatePDF') || 'Generate PDF'}
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          alert('Full report data not available for this audit. Please run a new audit to generate a PDF report.');
                        }}
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                        title="Full report data not available"
                      >
                        <Download size={16} />
                        {t('reporting.generatePDF') || 'Generate PDF'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-slate-800 rounded-2xl border border-slate-700 p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="text-emerald-400" size={20} />
            {t('reporting.howTo') || 'How to Generate Reports'}
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-slate-300 text-sm">
            <li>{t('reporting.step1') || 'Run a site audit using the Site Audit tool'}</li>
            <li>{t('reporting.step2') || 'View the detailed results on the audit results page'}</li>
            <li>{t('reporting.step3') || 'Click "Download PDF Report" to generate a comprehensive PDF'}</li>
            <li>{t('reporting.step4') || 'The PDF will include all SEO scores, issues, and recommendations'}</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

